import { useMemo, useState } from 'react';
import { Field } from '../components/ui/bits.jsx';
import { useUI } from '../components/ui/feedback.jsx';
import { Icon } from '../components/ui/Icon.jsx';
import { ustvariSolo } from '../lib/api.js';
import { parseProgramText, sklopiIzBesedila } from '../lib/program.js';
import { t } from '../i18n/index.js';

/*
  Inštruktor ali lastnik šole po Google prijavi ustvari svojo šolo — brez
  čakanja na platformo. Vpiše ime, državo, jezik in program (kot besedilo);
  Učilnica nastane takoj s 30-dnevnim preizkusom, on je lastnik.
*/

const DRZAVE = ['SI', 'HR', 'AT', 'IT', 'DE', 'CH', 'CZ', 'SK', 'HU', 'PL', 'RS', 'BA', 'GB', 'FR', 'ES', 'PT', 'NL', 'BE', 'DK', 'SE', 'NO', 'FI'];

const PRIMER = `# Začetni tečaj
Vaja 1: Priprava padala
- Razprostiranje krila
- Pet kontrolnih točk
Vaja 2: Dvig krila
- Polnjenje in dvig krila
- Stabilizacija
# Nadaljevalni tečaj
Vaja 1: Zavoji
- Odprti zavoji
- Zaprti zavoji`;

export function UstvariSolo({ session, onDone, onBack }) {
  const { toast } = useUI();
  const [f, setF] = useState({ ime_sole: '', kontakt_ime: '', drzava: 'SI', jezik: 'sl', telefon: '', splet: '', program: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const rows = useMemo(() => (f.program.trim() ? parseProgramText(f.program) : []), [f.program]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.ime_sole.trim().length < 2) {
      toast.bad('Vpiši ime šole.');
      return;
    }
    setBusy(true);
    try {
      const res = await ustvariSolo({
        ...f,
        email: session.user.email,
        program: rows,
        sklopi: sklopiIzBesedila(f.program),
        program_besedilo: f.program,
        vir: 'aplikacija',
      });
      toast.ok(`Šola ${f.ime_sole.trim()} je ustvarjena. Koda za vpis tečajnikov: ${res?.koda ?? ''}.`, { duration: 10000 });
      await onDone();
    } catch (err) {
      toast.bad(err.message ?? 'Šole ni bilo mogoče ustvariti.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card card--pad stack" onSubmit={submit} noValidate>
      <div className="stack stack--tight">
        <h2 className="t-lg">{t('Ustvari svojo šolo')}</h2>
        <p className="t-sm t-dim">
          {t('Postaneš lastnik šole. Preizkus traja 30 dni, do 10 tečajnikov, vse funkcije. Program lahko kadarkoli dopolniš.')}
        </p>
      </div>

      <div className="grid-2">
        <Field label={t('Ime šole')} id="us-ime" required>
          <input id="us-ime" className="input" value={f.ime_sole} onChange={set('ime_sole')} />
        </Field>
        <Field label={t('Tvoje ime')} id="us-kontakt">
          <input id="us-kontakt" className="input" value={f.kontakt_ime} onChange={set('kontakt_ime')} autoComplete="name" />
        </Field>
        <Field label={t('Država')} id="us-drz">
          <select id="us-drz" className="input" value={f.drzava} onChange={set('drzava')}>
            {DRZAVE.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('Jezik vmesnika')} id="us-jez">
          <select id="us-jez" className="input" value={f.jezik} onChange={set('jezik')}>
            <option value="sl">Slovenščina</option>
            <option value="en">English</option>
          </select>
        </Field>
        <Field label={t('Telefon')} id="us-tel">
          <input id="us-tel" className="input" type="tel" value={f.telefon} onChange={set('telefon')} />
        </Field>
        <Field label={t('Spletna stran')} id="us-splet">
          <input id="us-splet" className="input" value={f.splet} onChange={set('splet')} />
        </Field>
      </div>

      <Field
        label={t('Program šolanja')}
        id="us-prog"
        hint={
          rows.length
            ? t('Prebranih {n} vaj. Prazno = Perutov program kot izhodišče.', { n: rows.length })
            : t('Vrstica = vaja, vrstica z vezajem = pod-vaja, vrstica z # = sklop. Prazno = Perutov program kot izhodišče.')
        }
      >
        <textarea
          id="us-prog"
          className="input"
          rows={9}
          value={f.program}
          onChange={set('program')}
          placeholder={PRIMER}
          style={{ fontFamily: 'var(--font-num)', fontSize: 'var(--fs-sm)' }}
        />
      </Field>

      <p className="t-sm t-dim">
        {t('Lastnik šole si s tem Google računom:')} <strong>{session.user.email}</strong>
      </p>

      <div className="row row--wrap">
        <button className="btn btn--primary btn--lg" type="submit" disabled={busy}>
          {busy ? t('Ustvarjam …') : t('Ustvari šolo')} {!busy && <Icon name="right" size={16} />}
        </button>
        <button className="btn btn--ghost" type="button" onClick={onBack}>
          {t('Nazaj')}
        </button>
      </div>
    </form>
  );
}
